import React, { useState, useMemo } from 'react'
import {
  useReactTable,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  flexRender,
  createColumnHelper,
  ColumnDef,
  SortingState,
  ColumnFiltersState,
  PaginationState,
  ColumnResizeMode,
} from '@tanstack/react-table'
import { Button } from './button'
import { Input } from './input'
import { 
  ChevronLeft, 
  ChevronRight, 
  ChevronsLeft, 
  ChevronsRight,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Search,
  X,
  Filter
} from 'lucide-react'

interface DataTableProps<TData> {
  data: TData[]
  columns: ColumnDef<TData, any>[]
  enableFiltering?: boolean
  enablePagination?: boolean
  enableSorting?: boolean
  enableEditing?: boolean
  onDataChange?: (data: TData[]) => void
  className?: string
  isDarkMode?: boolean
}

export function DataTable<TData>({
  data,
  columns,
  enableFiltering = true,
  enablePagination = true,
  enableSorting = true,
  enableEditing = false,
  onDataChange,
  className = '',
  isDarkMode = false,
}: DataTableProps<TData>) {
  const [sorting, setSorting] = useState<SortingState>([])
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [showColumnFilters, setShowColumnFilters] = useState(false)
  const [columnResizeMode, setColumnResizeMode] = useState<ColumnResizeMode>('onChange')
  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  })

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    getFilteredRowModel: enableFiltering ? getFilteredRowModel() : undefined,
    getSortedRowModel: enableSorting ? getSortedRowModel() : undefined,
    getPaginationRowModel: enablePagination ? getPaginationRowModel() : undefined,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setGlobalFilter,
    onPaginationChange: setPagination,
    columnResizeMode,
    enableColumnResizing: true,
    state: {
      sorting,
      columnFilters,
      globalFilter,
      pagination,
    },
  })

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'ArrowUp' || event.key === 'ArrowDown') {
      event.preventDefault()
      // Add keyboard navigation logic here
    }
  }

  return (
    <div className={`w-full ${className}`}>
      {/* Table */}
      <div className={`rounded-md border ${isDarkMode ? 'border-gray-600' : 'border-gray-200'}`}>
        {/* Search and Filter Controls */}
        {enableFiltering && (
          <div className={`flex items-center gap-2 p-3 border-b ${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'}`}>
            <div className="relative flex-1 max-w-sm">
              <Search className={`absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`} />
              <Input
                placeholder="Search all columns..."
                value={globalFilter}
                onChange={(e) => setGlobalFilter(e.target.value)}
                className="pl-10"
              />
              {globalFilter && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="absolute right-1 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0"
                  onClick={() => setGlobalFilter('')}
                >
                  <X className="h-3 w-3" />
                </Button>
              )}
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowColumnFilters(!showColumnFilters)}
              className={`flex items-center gap-2 ${
                showColumnFilters ? 'bg-orange-100 border-orange-300 text-orange-700' : ''
              }`}
              style={showColumnFilters ? { backgroundColor: 'rgba(255, 134, 53, 0.4)' } : {}}
            >
              <Filter className="h-4 w-4" />
              Filters
              {showColumnFilters && <span className="text-xs">(Active)</span>}
            </Button>
          </div>
        )}
        <div className="overflow-auto max-h-[600px]">
          <table className="w-full" style={{ width: table.getCenterTotalSize(), tableLayout: 'fixed' }}>
            <thead className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'} sticky top-0`}>
              {table.getHeaderGroups().map((headerGroup) => (
                <React.Fragment key={headerGroup.id}>
                  {/* Header Row */}
                  <tr>
                    {headerGroup.headers.map((header) => (
                      <th
                        key={header.id}
                        className={`border-b ${isDarkMode ? 'border-gray-600 text-gray-300' : 'border-gray-200 text-gray-500'} px-4 py-3 text-left text-xs font-medium uppercase tracking-wider relative`}
                        style={{ width: header.getSize() }}
                      >
                        {header.isPlaceholder ? null : (
                          <div className="flex items-center gap-1">
                            <div
                              className={`flex items-center gap-1 ${
                                header.column.getCanSort() ? 'cursor-pointer select-none' : ''
                              }`}
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                              {enableSorting && header.column.getCanSort() && (
                                <span className="ml-1">
                                  {header.column.getIsSorted() === 'desc' ? (
                                    <ArrowDown className="h-3 w-3" />
                                  ) : header.column.getIsSorted() === 'asc' ? (
                                    <ArrowUp className="h-3 w-3" />
                                  ) : (
                                    <ArrowUpDown className="h-3 w-3 text-gray-400" />
                                  )}
                                </span>
                              )}
                            </div>
                          </div>
                        )}
                        {/* Column Resizer */}
                        {header.column.getCanResize() && (
                          <div
                            {...{
                              onMouseDown: header.getResizeHandler(),
                              onTouchStart: header.getResizeHandler(),
                              className: `absolute right-0 top-0 h-full w-2 cursor-col-resize flex items-center justify-center select-none`,
                              style: {
                                userSelect: 'none',
                              },
                            }}
                          >
                            <div
                              className={`w-px bg-gray-300 hover:bg-blue-500 transition-colors pointer-events-none ${
                                header.column.getIsResizing() ? 'bg-blue-500' : ''
                              }`}
                              style={{ 
                                marginTop: '4px', 
                                marginBottom: '4px',
                                height: 'calc(100% - 8px)'
                              }}
                            />
                          </div>
                        )}
                      </th>
                    ))}
                  </tr>
                  {/* Filter Row */}
                  {enableFiltering && showColumnFilters && (
                    <tr className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'} border-b`}>
                      {headerGroup.headers.map((header) => (
                        <th
                          key={`filter-${header.id}`}
                          className="px-4 py-2"
                          style={{ width: header.getSize() }}
                        >
                          {header.id !== 'actions' ? (
                            <Input
                              placeholder="Filter..."
                              value={(header.column.getFilterValue() as string) ?? ''}
                              onChange={(e) => header.column.setFilterValue(e.target.value)}
                              className="h-7 text-xs border-orange-200 focus:border-orange-400"
                            />
                          ) : null}
                        </th>
                      ))}
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </thead>
            <tbody className={`${isDarkMode ? 'bg-gray-800 divide-gray-600' : 'bg-white divide-gray-200'} divide-y`}>
              {table.getRowModel().rows.map((row) => (
                <tr
                  key={row.id}
                  className={`${isDarkMode ? 'hover:bg-gray-700 focus-within:bg-gray-700' : 'hover:bg-gray-50 focus-within:bg-gray-50'}`}
                  onKeyDown={handleKeyDown}
                  tabIndex={0}
                >
                  {row.getVisibleCells().map((cell) => (
                    <td
                      key={cell.id}
                      className={`px-4 py-3 text-sm ${isDarkMode ? 'text-gray-100 border-gray-700' : 'text-gray-900 border-gray-100'} border-b`}
                      style={{ width: cell.column.getSize() }}
                    >
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {enablePagination && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Showing {table.getState().pagination.pageIndex * table.getState().pagination.pageSize + 1} to{' '}
              {Math.min(
                (table.getState().pagination.pageIndex + 1) * table.getState().pagination.pageSize,
                table.getFilteredRowModel().rows.length
              )}{' '}
              of {table.getFilteredRowModel().rows.length} entries
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.setPageIndex(0)}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronsLeft className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
            >
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Page {table.getState().pagination.pageIndex + 1} of {table.getPageCount()}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
            >
              <ChevronRight className="h-4 w-4" />
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.setPageIndex(table.getPageCount() - 1)}
              disabled={!table.getCanNextPage()}
            >
              <ChevronsRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

export { createColumnHelper }